import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { connect } from 'react-redux';
import { X, Tag } from 'lucide-react';
import { getTags } from '../../actions/tagActions';
import SubmissionFormFormSkeleton from './views/FormView/SubmissionFormFormSkeleton';

// Register custom ActionButton Blot so Quill preserves styled button links
const Quill = ReactQuill.Quill;
const BlockEmbed = Quill.import('blots/block/embed');

class ActionButtonBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    const anchor = document.createElement('a');
    anchor.setAttribute('href', value.url || '#');
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
    anchor.textContent = value.text || 'Click Here';
    anchor.style.cssText = 'display:inline-block;padding:12px 28px;background-color:#1086a4;color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;font-size:14px;text-align:center;cursor:pointer;';
    node.appendChild(anchor);
    node.setAttribute('contenteditable', 'false');
    node.style.cssText = 'margin:4px 0;';
    return node;
  }

  static value(node) {
    const anchor = node.querySelector('a');
    return {
      url: anchor ? anchor.getAttribute('href') : '',
      text: anchor ? anchor.textContent : ''
    };
  }
}

ActionButtonBlot.blotName = 'actionButton';
ActionButtonBlot.tagName = 'div';
ActionButtonBlot.className = 'ql-action-btn-wrapper';
Quill.register(ActionButtonBlot, true);

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video',
  'actionButton'
];

const SubmissionFormForm = ({ mode = 'new', submissionForm = null, onSubmit, onClose, tag: { tags }, getTags }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkRequired, setLinkRequired] = useState(true);
  const [commentRequired, setCommentRequired] = useState(true);
  const [achievementImageRequired, setAchievementImageRequired] = useState(false);
  const [fullNameRequired, setFullNameRequired] = useState(false);
  const [universityRequired, setUniversityRequired] = useState(false);
  const [videoRequired, setVideoRequired] = useState(false);
  const [videoInstructions, setVideoInstructions] = useState('');
  const [courseId, setCourseId] = useState('');
  const [automaticApproval, setAutomaticApproval] = useState(false);
  const [repeatable, setRepeatable] = useState(false);
  const [collaboratorsEnabled, setCollaboratorsEnabled] = useState(true);
  const [experiencePoints, setExperiencePoints] = useState(0);
  const [embedUrls, setEmbedUrls] = useState(['']);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const quillRef = useRef(null);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['insertButton'],
        ['clean']
      ],
      handlers: {
        insertButton: function() {
          const url = prompt('Enter the URL:');
          if (!url) return;
          const text = prompt('Enter button text:', 'Click Here');
          if (!text) return;

          const editor = this.quill;
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'actionButton', { url, text }, 'user');
          editor.setSelection(range.index + 1, 'silent');
        }
      }
    }
  }), []);

  useEffect(() => {
    getTags();
  }, [getTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        if (submissionForm) {
          setTitle(submissionForm.title || '');
          setDescription(submissionForm.description || '');
          setLinkRequired(submissionForm.linkRequired !== false);
          setCommentRequired(submissionForm.commentRequired !== false);
          setAchievementImageRequired(submissionForm.achievementImageRequired || false);
          setFullNameRequired(submissionForm.fullNameRequired || false);
          setUniversityRequired(submissionForm.universityRequired || false);
          setVideoRequired(submissionForm.videoRequired || false);
          setVideoInstructions(submissionForm.videoInstructions || '');
          setCourseId(submissionForm.courseId || '');
          setAutomaticApproval(submissionForm.automaticApproval || false);
          setRepeatable(submissionForm.repeatable || false);
          setCollaboratorsEnabled(submissionForm.collaboratorsEnabled !== false);
          setExperiencePoints(submissionForm.experiencePoints || 0);
          setEmbedUrls(submissionForm.embedUrls?.length ? submissionForm.embedUrls : (submissionForm.embedUrl ? [submissionForm.embedUrl] : ['']));
          setSelectedTags(submissionForm.tags ? submissionForm.tags.map(tag => tag._id) : []);
        } else {
          setTitle('');
          setDescription('');
          setLinkRequired(true);
          setCommentRequired(true);
          setAchievementImageRequired(false);
          setFullNameRequired(false);
          setUniversityRequired(false);
          setCourseId('');
          setAutomaticApproval(false);
          setRepeatable(false);
          setCollaboratorsEnabled(true);
          setExperiencePoints(0);
          setEmbedUrls(['']);
          setSelectedTags([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeForm();
  }, [submissionForm]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    const formData = {
      title: title.trim(),
      description: description.trim(),
      linkRequired,
      commentRequired,
      achievementImageRequired,
      universityRequired,
      videoRequired,
      videoInstructions: videoRequired ? videoInstructions.trim() : '',
      courseId: courseId || null,
      automaticApproval,
      repeatable,
      collaboratorsEnabled,
      experiencePoints,
      embedUrls: embedUrls.filter(u => u.trim()),
      tags: selectedTags
    };

    if (onSubmit) onSubmit(formData);
    if (isNew) {
      setTitle('');
      setDescription('');
      setLinkRequired(true);
      setCommentRequired(true);
      setAchievementImageRequired(false);
      setFullNameRequired(false);
      setUniversityRequired(false);
      setVideoRequired(false);
      setVideoInstructions('');
      setCourseId('');
      setExperiencePoints(0);
      setEmbedUrls(['']);
      setSelectedTags([]);
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(id => id !== tagId));
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return tags.filter(tag => 
      !selectedTags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-[#054581]">
          {isNew && 'Add New Submission Form'}
          {isEdit && 'Edit Submission Form'}
          {isView && 'View Submission Form'}
        </h3>
        <button
          onClick={onClose}
          type="button"
          className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {isLoading ? (
        <SubmissionFormFormSkeleton />
      ) : (
        <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-[#054581] mb-2">
                Form Title *
              </label>
              <input
                type="text"
                id="title"
                placeholder="Enter submission form title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                required
                disabled={isView}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-[#054581] mb-2">
                Description
              </label>
              <div className="border border-[#1086a4] rounded-md overflow-hidden" style={{ height: '200px' }}>
                <ReactQuill
                  ref={quillRef}
                  value={description}
                  onChange={setDescription}
                  modules={quillModules}
                  formats={quillFormats}
                  className="bg-[#f9fafb] h-full flex flex-col"
                  readOnly={isView}
                  placeholder="Enter form description"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}
                />
                <style>
                  {`
                    .ql-container {
                      flex: 1;
                      overflow: auto;
                      height: auto !important;
                    }
                    .ql-toolbar {
                      position: sticky;
                      top: 0;
                      z-index: 1;
                      background: #f9fafb;
                    }
                    .ql-insertButton::before {
                      content: '🔘';
                      font-size: 16px;
                    }
                    .ql-insertButton {
                      width: auto !important;
                      padding: 0 5px !important;
                    }
                  `}
                </style>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#054581] mb-2">
                Embed URLs
              </label>
              <div className="space-y-2">
                {embedUrls.map((url, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="url"
                      placeholder="Paste a Google Form, Google Sheet, Monday form, or PDF link"
                      value={url}
                      onChange={e => {
                        const updated = [...embedUrls];
                        updated[index] = e.target.value;
                        setEmbedUrls(updated);
                      }}
                      className="flex-1 px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                      disabled={isView}
                    />
                    {!isView && embedUrls.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setEmbedUrls(embedUrls.filter((_, i) => i !== index))}
                        className="text-red-400 hover:text-red-600 transition-colors p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {!isView && (
                  <button
                    type="button"
                    onClick={() => setEmbedUrls([...embedUrls, ''])}
                    className="text-sm text-[#1086a4] hover:text-[#054581] font-medium transition-colors"
                  >
                    + Add another embed
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-500 mt-1 block">
                Embed forms, spreadsheets, or PDFs for students to view inside the submission. Supports Google Forms, Google Sheets, Monday.com, and any embeddable URL.
              </span>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={commentRequired}
                  onChange={e => setCommentRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Comment Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={linkRequired}
                  onChange={e => setLinkRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Link Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={achievementImageRequired}
                  onChange={e => setAchievementImageRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">Image Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={universityRequired}
                  onChange={e => setUniversityRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">University Required</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={videoRequired}
                  onChange={e => setVideoRequired(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Video Recording Required
                  <span className="ml-1 text-xs text-gray-500">
                    (Students record up to 2 minutes with their camera)
                  </span>
                </span>
              </label>
              {videoRequired && (
                <textarea
                  value={videoInstructions}
                  onChange={e => setVideoInstructions(e.target.value)}
                  rows={3}
                  className="mt-2 w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                  placeholder="What should the video include? e.g. Introduce yourself and describe your career goal."
                  disabled={isView}
                />
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={automaticApproval}
                  onChange={e => setAutomaticApproval(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Automatic Approval
                  <span className="ml-1 text-xs text-gray-500">
                    (Submissions will be automatically approved without manual review)
                  </span>
                </span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={repeatable}
                  onChange={e => setRepeatable(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Repeatable Submission Form
                  <span className="ml-1 text-xs text-gray-500">
                    (Allow users to submit multiple times)
                  </span>
                </span>
              </label>
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={collaboratorsEnabled}
                  onChange={e => setCollaboratorsEnabled(e.target.checked)}
                  className="rounded border-[#1086a4] text-[#1086a4] focus:ring-[#1086a4]"
                  disabled={isView}
                />
                <span className="text-sm font-medium text-[#054581]">
                  Allow Collaborators
                  <span className="ml-1 text-xs text-gray-500">
                    (Let students add collaborators to their submission)
                  </span>
                </span>
              </label>
            </div>

            <div>
              <label htmlFor="experiencePoints" className="block text-sm font-medium text-[#054581] mb-2">
                Experience Points
              </label>
              <input
                type="number"
                id="experiencePoints"
                min="0"
                max="1000"
                value={experiencePoints}
                onChange={e => setExperiencePoints(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                placeholder="0"
                disabled={isView}
              />
              <span className="text-xs text-gray-500 mt-1">
                Points awarded when mission is completed (default: 0)
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#054581] mb-2">
                Tags
              </label>
              {!isView && (
                <div className="relative" ref={tagDropdownRef}>
                  <div
                    onClick={handleTagInputClick}
                    className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
                  >
                    <div className="flex flex-wrap items-center gap-1">
                      {getSelectedTagObjects().map(tag => (
                        <span
                          key={tag._id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag.title}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTag(tag._id);
                            }}
                            className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={tagSearchTerm}
                        onChange={(e) => setTagSearchTerm(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search tags..."}
                        className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                      />
                    </div>
                  </div>
                  {showTagDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                      {getFilteredTags().length > 0 ? (
                        getFilteredTags().map(tag => (
                          <button
                            key={tag._id}
                            type="button"
                            onClick={() => toggleTag(tag._id)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                          >
                            <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                            {tag.title}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isView && getSelectedTagObjects().length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {getSelectedTagObjects().map(tag => (
                    <span
                      key={tag._id}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag.title}
                    </span>
                  ))}
                </div>
              )}
            </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
            >
              Cancel
            </button>
            {!isView && (
              <button
                type="submit"
                className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                {isNew ? 'Add Form' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = state => ({
  tag: state.tag
});

export default connect(mapStateToProps, { getTags })(SubmissionFormForm); 